import { AxiosService } from 'src/axios/axios.service';
import { InsufficientRoleError } from 'src/services/errors/InsufficientRoleError.ts';
import { NoPaymentMethodError } from 'src/services/errors/NoPaymentMethodError.ts';
import { SessionInvalidError } from 'src/services/errors/SessionInvalidError.ts';
import {
  TRIAL_PURCHASE_RESTRICTED_CODE,
  TrialPurchaseRestrictedError,
} from 'src/services/errors/TrialPurchaseRestrictedError.ts';
import { UserNotInWorkspaceError } from 'src/services/errors/UserNotInWorkspaceError.ts';
import { WorkspaceLookupError } from 'src/services/errors/WorkspaceLookupError.ts';
import {
  tCreditPackage,
  tCreditsBalance,
  tPaginated,
  tPurchaseHistoryItem,
} from 'src/pages/WhatsappApi/WhatsappApi.types.ts';

/**
 * GRO-811 Credits service — BFF read-side companion to `OneTimeCredits.service`.
 *
 * Per design ADR #3 (split BFF read / Billing write):
 *   - This module owns BFF reads: catalog, balance, purchase history.
 *   - `OneTimeCredits.service` keeps the Billing-direct writes
 *     (`purchaseCredits`, `getPaymentMethods`).
 *
 * Status-code mapping (ADR #6, Phase 2.4):
 *   - 401 (any sub-code) → `SessionInvalidError` (thrown)
 *   - 402 → `tCreditsServiceError` with `type: 'decline'` + i18n key
 *   - 403 `insufficient_role` → `InsufficientRoleError` (thrown)
 *   - 403 `user_not_in_workspace` → `UserNotInWorkspaceError` (thrown)
 *   - 409 → `NoPaymentMethodError` (thrown)
 *   - 422 `trial_workspace_restriction` → `TrialPurchaseRestrictedError` (thrown)
 *   - 422 (other) → `tCreditsServiceError` with `type: 'validation'` + field errors
 *   - 502 `workspace_lookup_failed` → `WorkspaceLookupError` (thrown)
 *   - 5xx (other) → `tCreditsServiceError` with `type: 'unexpected'` + i18n key
 *
 * AuthRole error shape (leadbilling commit 6686e338):
 *   `{ success: false, data: [], message: "...", errors: { code: "<machine_code>" } }`
 *
 * `getCreditPackages` now consumes the Stripe-driven payload from the BFF
 * (`{ packages, unit_price_cents, currency }`). The legacy 404 fallback to
 * `ONE_TIME_CREDIT_TIERS` is gone — the endpoint is always available.
 */

// --- Error types --------------------------------------------------------

export type tCreditsServiceErrorType =
  | 'decline'
  | 'validation'
  | 'unexpected';

export type tCreditsFieldErrors = Record<string, string[]>;

export type tCreditsServiceError = Error & {
  /** Discriminator — caller branches on this. */
  readonly type: tCreditsServiceErrorType;
  /** i18n key for toast/banner copy (consumer translates). */
  readonly i18nKey?: string;
  /** Stripe decline_code echoed by the Billing service when 402. */
  readonly declineCode?: string;
  /** Field-level Yup-style errors when 422. */
  readonly fieldErrors?: tCreditsFieldErrors;
};

// Internal raw-response shapes. BFF returns `{ data: T }` envelopes.

type tPackagesEnvelope = {
  data: {
    packages: tCreditPackage[];
    unit_price_cents: number | null;
    currency: string | null;
  };
};

/** Shape returned by {@link CreditsService.getCreditPackages}. */
export type tCreditPackagesResult = {
  packages: tCreditPackage[];
  /**
   * Per-credit cost in the workspace currency (cents). Drives the
   * custom-amount input's live total. Null when the Stripe Credit Unit
   * Product is not yet configured for this currency — the UI should
   * disable the custom path in that case.
   */
  unitPriceCents: number | null;
  /** Workspace currency (e.g. "MXN", "USD"). Null when no active subscription. */
  currency: string | null;
};

type tBalanceEnvelope = {
  data: tCreditsBalance;
};

type tPurchasesEnvelope = {
  data: {
    data: tPurchaseHistoryItem[];
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
};

/**
 * The `errors` envelope is overloaded across two backend shapes:
 *   - Legacy 422 from BFF: `Record<string, string[]>` (Yup-style field errors).
 *   - AuthRole middleware (401/403/502): `{ code: string }`.
 *
 * We accept both at the wire level and narrow downstream.
 */
type tAuthRoleErrorEnvelope = { code?: string };

type tAxiosLikeError = {
  isAxiosError: boolean;
  response?: {
    status: number;
    data?: {
      decline_code?: string;
      errors?: tCreditsFieldErrors | tAuthRoleErrorEnvelope;
      /**
       * Flat top-level machine code. Distinct from the plural `errors`
       * field-bag: leadbilling emits this singular form for domain rejections
       * that are not field validation (today only the BUG-527 trial gate).
       */
      error?: string;
    };
  };
};

const isAxiosLikeError = (e: unknown): e is tAxiosLikeError =>
  typeof e === 'object' &&
  e !== null &&
  'isAxiosError' in e &&
  (e as tAxiosLikeError).isAxiosError === true;

/**
 * Extract the AuthRole `errors.code` machine code if present. Returns
 * `undefined` when the envelope shape is the legacy field-errors map.
 */
const extractAuthRoleCode = (
  errors: tCreditsFieldErrors | tAuthRoleErrorEnvelope | undefined,
): string | undefined => {
  if (!errors || typeof errors !== 'object') return undefined;
  const candidate = (errors as tAuthRoleErrorEnvelope).code;
  return typeof candidate === 'string' ? candidate : undefined;
};

/**
 * Narrow `errors` to the legacy field-errors shape (Record<string, string[]>).
 * The AuthRole `{ code }` shape is filtered out so the 422 branch never
 * misreads it as field errors.
 */
const extractFieldErrors = (
  errors: tCreditsFieldErrors | tAuthRoleErrorEnvelope | undefined,
): tCreditsFieldErrors | undefined => {
  if (!errors || typeof errors !== 'object') return undefined;
  if (extractAuthRoleCode(errors) !== undefined) return undefined;
  return errors as tCreditsFieldErrors;
};

const buildClassifiedError = (
  type: tCreditsServiceErrorType,
  message: string,
  extras?: {
    i18nKey?: string;
    declineCode?: string;
    fieldErrors?: tCreditsFieldErrors;
  },
): tCreditsServiceError =>
  Object.assign(new Error(message), {
    type,
    i18nKey: extras?.i18nKey,
    declineCode: extras?.declineCode,
    fieldErrors: extras?.fieldErrors,
  }) as tCreditsServiceError;

/**
 * Map an axios-like error to either:
 *   - `NoPaymentMethodError` (thrown for 409)
 *   - `TrialPurchaseRestrictedError` (thrown for 422 `trial_workspace_restriction`)
 *   - `tCreditsServiceError` (thrown for 402/422/5xx)
 *   - the original error (rethrown — network/other)
 *
 * Exported so `OneTimeCredits.service.purchaseCredits` (Billing writes) can
 * share the same mapping in Phase 2.5.
 */
export const mapCreditsServiceError = (rawError: unknown): never => {
  if (!isAxiosLikeError(rawError)) {
    throw rawError;
  }
  const status = rawError.response?.status;
  const declineCode = rawError.response?.data?.decline_code;
  const errorsField = rawError.response?.data?.errors;
  const authRoleCode = extractAuthRoleCode(errorsField);
  const fieldErrors = extractFieldErrors(errorsField);

  // 401 — any sub-code (`missing_authorization_header`,
  // `malformed_authorization_header`, `invalid_token`) collapses to a single
  // user-facing message: "your session is invalid, please sign in again."
  if (status === 401) {
    throw new SessionInvalidError(undefined, authRoleCode);
  }
  // 403 — branch by AuthRole machine code.
  if (status === 403) {
    if (authRoleCode === 'insufficient_role') {
      throw new InsufficientRoleError();
    }
    if (authRoleCode === 'user_not_in_workspace') {
      throw new UserNotInWorkspaceError();
    }
    // Unknown 403 sub-code — fall through to generic unexpected so the user
    // still sees a coherent toast rather than a silent drop.
    throw buildClassifiedError('unexpected', 'unexpected_error', {
      i18nKey: 'page.credits.errors.unexpected',
    });
  }
  if (status === 409) {
    throw new NoPaymentMethodError();
  }
  if (status === 402) {
    throw buildClassifiedError('decline', 'payment_declined', {
      i18nKey: 'page.credits.errors.declined',
      declineCode,
    });
  }
  if (status === 422) {
    // BUG-775 — the trial gate (BUG-527) rides the same 422 as ordinary field
    // validation but carries a flat `error` code instead of the `errors` bag.
    // It needs its own class because no retry can clear it: the purchase is
    // impossible until the subscription leaves TRIAL.
    if (rawError.response?.data?.error === TRIAL_PURCHASE_RESTRICTED_CODE) {
      throw new TrialPurchaseRestrictedError();
    }
    throw buildClassifiedError('validation', 'validation_error', {
      i18nKey: 'page.credits.errors.validation',
      fieldErrors,
    });
  }
  // 502 with `workspace_lookup_failed` is distinct from generic 5xx so the
  // user gets a "try again in a moment" message vs a generic crash toast.
  if (status === 502 && authRoleCode === 'workspace_lookup_failed') {
    throw new WorkspaceLookupError();
  }
  if (typeof status === 'number' && status >= 500) {
    throw buildClassifiedError('unexpected', 'unexpected_error', {
      i18nKey: 'page.credits.errors.unexpected',
    });
  }
  throw rawError;
};

// --- Public API ---------------------------------------------------------

export const CreditsService = Object.freeze({
  /**
   * GET /workspace/:wid/credits/packages — BFF.
   *
   * Post Stripe-only refactor (GRO-811): backend returns
   * `{ packages, unit_price_cents, currency }` so the front can drive both
   * the preset grid and the custom-amount input from a single round-trip.
   * `unit_price_cents` is null when the Credit Unit Product is not yet
   * configured for the workspace currency.
   *
   * The Stripe Product id is the `tCreditPackage.id` — selectors and the
   * purchase POST keep using `provider_price_id` for the Stripe Price id.
   */
  getCreditPackages: async (
    workspaceId: string,
  ): Promise<tCreditPackagesResult> => {
    const config = { isBFFService: true, headers: {} };
    try {
      const response = await AxiosService.get<tPackagesEnvelope>(
        `/workspace/${workspaceId}/credits/packages`,
        config,
      );
      const payload = response.data ?? {
        packages: [],
        unit_price_cents: null,
        currency: null,
      };
      return {
        packages: (payload.packages ?? []).map((pkg) => ({
          ...pkg,
          id: String(pkg.id),
        })),
        unitPriceCents: payload.unit_price_cents ?? null,
        currency: payload.currency ?? null,
      };
    } catch (rawError) {
      mapCreditsServiceError(rawError);
      // mapCreditsServiceError always throws; this satisfies TS control flow.
      return { packages: [], unitPriceCents: null, currency: null };
    }
  },

  /**
   * GET /workspace/:wid/credits/balance — BFF.
   * Returns the aggregate plan + additional balance shape consumed by
   * `CreditsTab` and `CreditsCard` (after Phase 3+).
   */
  getCreditsBalance: async (
    workspaceId: string,
  ): Promise<tCreditsBalance> => {
    const config = { isBFFService: true, headers: {} };
    try {
      const response = await AxiosService.get<tBalanceEnvelope>(
        `/workspace/${workspaceId}/credits/balance`,
        config,
      );
      return response.data;
    } catch (rawError) {
      mapCreditsServiceError(rawError);
      throw rawError; // unreachable — mapCreditsServiceError throws.
    }
  },

  /**
   * GET /workspace/:wid/credits/purchases?page=N&page_size=20 — BFF.
   * Default page size is 20 per design (`PurchaseHistoryModal` paginates
   * when rows > 20).
   */
  getPurchaseHistory: async (
    workspaceId: string,
    page = 1,
  ): Promise<tPaginated<tPurchaseHistoryItem>> => {
    const config = { isBFFService: true, headers: {} };
    const pageSize = 20;
    try {
      const response = await AxiosService.get<tPurchasesEnvelope>(
        `/workspace/${workspaceId}/credits/purchases?page=${page}&page_size=${pageSize}`,
        config,
      );
      const data = response.data;
      // Same id-coercion rationale as `getCreditPackages` — leadbilling emits
      // numeric DB IDs; the UI carries string ids end-to-end.
      const items = (data.data ?? []).map((row) => ({
        ...row,
        id: String(row.id),
      }));
      return {
        items,
        page: data.page ?? page,
        total_pages: data.total_pages ?? 0,
        total: data.total ?? 0,
      };
    } catch (rawError) {
      mapCreditsServiceError(rawError);
      throw rawError; // unreachable.
    }
  },
});
